/**
 * E002-T018: Input Tab Component
 * E002-T044: System-Filter Display
 * E002-T047: Batch Size Slider
 * E007-T022: Schema-based Input Mapping
 * E029-S020-8.20.1: Auto-Join Relations (Multi-Input)
 *
 * The automation's input settings for the settings panel. `part="input"`
 * renders two units: the primary table (source, status tracking, filter,
 * which rows) and below it the tables linked to it by relations;
 * `part="mapping"` renders the workflow input mapping: which column fills
 * each input the selected workflow asks for. Each setting is one
 * `SettingSection`, so the panel's tiles can point at it.
 *
 * A webhook is chosen in the trigger only: with a webhook trigger the source
 * shows that webhook and no further tables can be added.
 */

import { Button, Menu, MenuItem, MenuSeparator } from '@affine/component';
import { AnimatedPillSegment } from '@affine/core/components/animation/animated-pill-segment';
import { AnimatedRegion } from '@affine/core/components/animation/animated-region';
import { useI18n } from '@affine/i18n';
import {
  type InputTable,
  MAX_RELATION_TABLES_DISPLAY,
} from '@blocksuite/affine/blocks/streamlain';
import type { FilterGroup } from '@blocksuite/affine/data-view';
import { PlusIcon } from '@blocksuite/icons/rc';
import {
  changeRelatedInputTable,
  DEFAULT_INPUT_ORDER,
  type InputOrder,
} from '@streamlain/automation-safety-contract';
import { useLiveData, useService } from '@toeverything/infra';
import { nanoid } from 'nanoid';
import type { ChangeEvent, FC } from 'react';
import { useCallback, useMemo } from 'react';

import type { AutomationConfig, InputMapping } from '../services/automation';
import { DEFAULT_STATUS_TRACKING_CONFIG } from '../services/automation';
import type { ColumnConflict } from '../services/automation-discovery';
import type { ColumnInfo, DatabaseInfo } from '../services/database-discovery';
import { WorkflowService } from '../services/workflow';
import { TutorialRegistry } from '../tutorial/services/tutorial-registry';
import { DEFAULT_TRIGGER_CONFIG } from '../types';
import { needsStatusTracking } from './automation-input-requirements';
import * as panelStyles from './automation-settings-panel.css';
import { CollapsibleSection } from './components/collapsible-section';
import { InputMappingGrid } from './components/input-mapping-grid';
import { NativeFilterPanel } from './components/native-filter-panel';
import { useOpenWorkflow } from './components/open-workflow';
import {
  SectionHint,
  SectionNotice,
  SectionRemoveButton,
  SectionSkeleton,
  SectionStack,
  SectionTableRow,
} from './components/section-layout';
import { SettingSection, SettingUnit } from './components/setting-section';
import { SourceTableField } from './components/source-table-field';
import { StatusTrackingSection } from './components/status-tracking-section';
import { mappableInputs, runsOnceForAll } from './workflow-row-list';

type RowsMode = 'row' | 'batch';

/** The primary table and what is read from it, in the order they apply. */
const PRIMARY_TABLE_TILES = ['source', 'tracking', 'filter', 'rows'] as const;
const RELATED_TILES = ['related'] as const;

// Mirrored from backend AUTOMATION_BATCH_ROW_LIMIT. Backend remains authoritative.
const BATCH_LIMIT_MIN = 2;
const BATCH_LIMIT_MAX = 100;

const DEFAULT_BATCH_RETRY = {
  enabled: false,
  maxAttempts: 3,
  initialDelayMs: 1000,
  backoffMultiplier: 2,
  maxDelayMs: 30000,
};

function clampBatchLimit(value: number) {
  const safeValue = Number.isFinite(value)
    ? Math.floor(value)
    : BATCH_LIMIT_MAX;
  return Math.max(BATCH_LIMIT_MIN, Math.min(BATCH_LIMIT_MAX, safeValue));
}

export interface InputTabProps {
  /** `input`: source, related, filter, tracking, rows; `mapping`: input mapping. */
  part: 'input' | 'mapping';
  config: AutomationConfig;
  onUpdate: (updates: Partial<AutomationConfig>) => void;
  /** Source table columns (for resolving column names) */
  sourceColumns?: ColumnInfo[];
  /** E029-S020-8.20.1: All accessible databases (for relation lookup) */
  accessibleDatabases?: DatabaseInfo[];
  /** S020-8.20.6: Map of column IDs to conflict info (for status tracking) */
  usedColumns?: Map<string, ColumnConflict>;
  /** S020-8.20.6: Callback to navigate to conflicting automation */
  onNavigateToConflict?: (automationId: string, docId: string) => void;
  /** Create a status Select column in the primary input table */
  onCreateStatusColumn?: () => Promise<void> | void;
  /** Pauses the active schedule on the backend; resolves false on failure. */
  onDeactivateSchedule?: () => Promise<boolean>;
  /** Name of the trigger's webhook (webhook trigger only). */
  webhookName?: string | null;
  /**
   * Shows the source table on the canvas and starts a new relation column
   * there (only when the source table is on this canvas).
   */
  onAddRelationColumn?: () => void;
}

export const InputTab: FC<InputTabProps> = ({
  part,
  config,
  onUpdate,
  sourceColumns,
  accessibleDatabases,
  usedColumns,
  onNavigateToConflict,
  onCreateStatusColumn,
  onDeactivateSchedule,
  webhookName,
  onAddRelationColumn,
}) => {
  const t = useI18n();
  const registry = useService(TutorialRegistry);

  // E007-T022: Schema of the selected workflow (loaded by the workflow setting)
  const workflowService = useService(WorkflowService);
  const selectedSchema = useLiveData(workflowService.selectedWorkflowSchema$);
  const schemaLoadingMap = useLiveData(workflowService.schemaLoading$);

  // sl-za0v.13.2: Schema loading state for the current workflow
  // Default to true (assume loading) until explicitly set to false by loadWorkflowSchema.
  // Prevents flash of "schema load failed" before the first fetch completes.
  const isSchemaLoading = config.workflowId
    ? (schemaLoadingMap.get(config.workflowId) ?? true)
    : false;

  // The inputs columns fill; a row-list input receives the rows of the run.
  const mappingInput = selectedSchema
    ? mappableInputs(selectedSchema.input)
    : null;
  const hasSchema = !!(
    mappingInput?.required.length || mappingInput?.optional.length
  );

  // sl-za0v.13.2: Schema failed to load (workflow selected, not loading, no schema)
  const schemaLoadFailed =
    !!config.workflowId && !isSchemaLoading && !hasSchema && !selectedSchema;

  const triggerConfig = config.triggerConfig ?? DEFAULT_TRIGGER_CONFIG;
  const isWebhook = triggerConfig.type === 'webhook';

  // Check if status tracking is enabled for system filter display
  const statusTracking =
    config.statusTracking ?? DEFAULT_STATUS_TRACKING_CONFIG;
  const hasSystemFilter =
    statusTracking.enabled &&
    statusTracking.mode !== 'delete_row' &&
    !!statusTracking.columnId;

  // Get column name for system filter display
  const systemFilterColumnName = useMemo(() => {
    if (!hasSystemFilter || !sourceColumns) return null;
    return (
      sourceColumns.find(c => c.id === statusTracking.columnId)?.name ?? null
    );
  }, [hasSystemFilter, sourceColumns, statusTracking.columnId]);

  const primarySourceColumns = useMemo(() => {
    const primaryDb = accessibleDatabases?.find(
      db => db.blockId === config.sourceTableId
    );
    return primaryDb?.columns ?? sourceColumns ?? [];
  }, [accessibleDatabases, config.sourceTableId, sourceColumns]);

  // Filter handler - direct update from NativeFilterPanel (lead table)
  const handleFilterChange = useCallback(
    (newFilterGroup: FilterGroup) => {
      onUpdate({ filterGroup: newFilterGroup });
    },
    [onUpdate]
  );

  // Without workflow inputs the whole row is passed; an older column
  // selection narrows it until the user lifts it here.
  const handlePassWholeRow = useCallback(
    () => onUpdate({ selectedColumns: [] }),
    [onUpdate]
  );
  const openWorkflow = useOpenWorkflow(config);

  // E007-T022: Input mapping handler (schema mode)
  // E018-S002-T009: Only set inputMappings, clear selectedColumns to avoid redundancy
  const handleInputMappingsChange = useCallback(
    (inputMappings: InputMapping[]) => {
      onUpdate({ inputMappings, selectedColumns: [] });
    },
    [onUpdate]
  );

  // Relation columns of the source table that point back to it: the source
  // table cannot be its own related source.
  const selfRelationNames = useMemo(
    () =>
      (sourceColumns ?? []).flatMap(col =>
        col.type === 'relation' &&
        !!config.sourceTableId &&
        col.targetDatabaseId === config.sourceTableId
          ? [col.name]
          : []
      ),
    [sourceColumns, config.sourceTableId]
  );

  // Available relation columns from lead table (for "+ Add Related Table" dropdown)
  const availableRelations = useMemo(() => {
    if (!sourceColumns || !accessibleDatabases) return [];
    const alreadyLinked = new Set(
      (config.inputTables ?? []).flatMap(t =>
        t.linkedVia ? [t.linkedVia] : []
      )
    );
    return sourceColumns
      .flatMap(col => {
        if (
          col.type !== 'relation' ||
          !col.targetDatabaseId ||
          col.targetDatabaseId === config.sourceTableId ||
          alreadyLinked.has(col.id)
        ) {
          return [];
        }
        const targetDb = accessibleDatabases.find(
          db => db.blockId === col.targetDatabaseId
        );
        return [
          {
            columnId: col.id,
            columnName: col.name,
            targetTableId: col.targetDatabaseId,
            targetTableName: targetDb?.databaseTitle ?? col.name,
            targetDocId: col.targetDocId ?? targetDb?.docId,
            cardinality: col.cardinality ?? ('one' as const),
          },
        ];
      })
      .slice(0, MAX_RELATION_TABLES_DISPLAY);
  }, [
    sourceColumns,
    accessibleDatabases,
    config.inputTables,
    config.sourceTableId,
  ]);

  // Add a related table via relation column
  const handleAddRelatedTable = useCallback(
    (relation: (typeof availableRelations)[number]) => {
      const current = config.inputTables ?? [];
      const newTable: InputTable = {
        id: nanoid(),
        tableId: relation.targetTableId,
        tableName: relation.targetTableName,
        docId: relation.targetDocId,
        role: 'secondary',
        linkedVia: relation.columnId,
        cardinality: relation.cardinality,
      };
      onUpdate(
        changeRelatedInputTable(current, config.inputMappings ?? [], {
          operation: 'add',
          table: newTable,
        })
      );
    },
    [config.inputTables, config.inputMappings, onUpdate]
  );

  // Build the main input and persisted relations; unsupported drafts stay removable.
  const effectiveInputTables = useMemo((): InputTable[] => {
    const allSecondaries = (config.inputTables ?? []).filter(
      t => t.role === 'secondary'
    );
    const lead: InputTable = {
      id: 'lead',
      tableId: config.sourceTableId ?? '',
      tableName: config.sourceTableName ?? '',
      docId: config.sourceDocId ?? undefined,
      role: 'lead',
      filterGroup: config.filterGroup,
    };
    return [lead, ...allSecondaries];
  }, [
    config.sourceTableId,
    config.sourceTableName,
    config.sourceDocId,
    config.filterGroup,
    config.inputTables,
  ]);

  // sl-m379: Build column groups for InputMappingGrid
  const secondaryColumnGroups = useMemo(() => {
    if (!accessibleDatabases) return [];
    const secondaries = effectiveInputTables.filter(
      t => t.role === 'secondary'
    );
    return secondaries
      .filter(t => t.linkedVia)
      .map(t => {
        const db = accessibleDatabases.find(d => d.blockId === t.tableId);
        return {
          inputTableId: t.id,
          linkedVia: t.linkedVia,
          tableId: t.tableId,
          label: t.tableName,
          columns: db?.columns ?? [],
          // sl-i6t9: Pass cardinality for visual indicator in mapping grid
          cardinality: t.cardinality,
        };
      });
  }, [effectiveInputTables, accessibleDatabases]);

  // All secondary tables (related + free) from persisted config
  const secondaryTables = useMemo(
    () => (config.inputTables ?? []).filter(t => t.role === 'secondary'),
    [config.inputTables]
  );

  // Remove a relation binding or an invalid unlinked input explicitly
  const handleRemoveSecondaryTable = useCallback(
    (id: string) => {
      const current = config.inputTables ?? [];
      onUpdate(
        changeRelatedInputTable(current, config.inputMappings ?? [], {
          operation: 'remove',
          id,
        })
      );
    },
    [config.inputTables, config.inputMappings, onUpdate]
  );

  // T047: "Which rows" — the next matching row per run (row mode), or all
  // matching rows as one list, at most `batchSize` (batch mode). A lower
  // limit keeps runs small for rate-limited or costly workflows.
  const rowsMode: RowsMode =
    config.batchConfig?.executionMode === 'batch' ? 'batch' : 'row';
  const batchLimit = clampBatchLimit(
    config.batchConfig?.batchSize ?? BATCH_LIMIT_MAX
  );

  const updateBatch = useCallback(
    (executionMode: RowsMode, batchSize: number) =>
      onUpdate({
        batchConfig: {
          ...config.batchConfig,
          batchSize,
          executionMode,
          retry: config.batchConfig?.retry ?? { ...DEFAULT_BATCH_RETRY },
          onFailure: config.batchConfig?.onFailure ?? 'stop',
        },
      }),
    [config.batchConfig, onUpdate]
  );

  const handleRowsMode = useCallback(
    (mode: RowsMode) => {
      if (mode === rowsMode) return;
      updateBatch(mode, mode === 'batch' ? BATCH_LIMIT_MAX : 1);
    },
    [rowsMode, updateBatch]
  );

  const handleBatchLimit = useCallback(
    (event: ChangeEvent<HTMLInputElement>) =>
      updateBatch('batch', clampBatchLimit(Number(event.currentTarget.value))),
    [updateBatch]
  );

  // The order in which runs take the rows the filter selects (by creation).
  const orderDirection = (config.inputOrder ?? DEFAULT_INPUT_ORDER).direction;
  const handleOrder = useCallback(
    (direction: InputOrder['direction']) => {
      if (direction === orderDirection) return;
      onUpdate({ inputOrder: { by: 'created', direction } });
    },
    [onUpdate, orderDirection]
  );

  if (part === 'mapping')
    return (
      <SettingSection tile="mapping" section="fields">
        <CollapsibleSection
          title={t['com.streamlain.automation.panel.tile.mapping']()}
          onInfoClick={() => registry.start('automation:inputFields')}
        >
          {!config.workflowId ? (
            <SectionHint>
              {t['com.streamlain.automation.sidebar.mapping.needs-workflow']()}
            </SectionHint>
          ) : isSchemaLoading ? (
            <SectionSkeleton />
          ) : schemaLoadFailed ? (
            <SectionNotice variant="warning">
              {t['com.streamlain.streamlain.input.schema-load-failed']()}
            </SectionNotice>
          ) : hasSchema && mappingInput ? (
            <div data-coachmark-target="input-mapping-grid">
              <InputMappingGrid
                schemaFields={mappingInput}
                sourceColumns={sourceColumns ?? []}
                mappings={config.inputMappings ?? []}
                onMappingsChange={handleInputMappingsChange}
                secondaryColumnGroups={secondaryColumnGroups}
                sourceTableName={config.sourceTableName ?? undefined}
                webhookGroup={
                  isWebhook
                    ? {
                        name:
                          webhookName ??
                          t[
                            'com.streamlain.automation.sidebar.trigger.webhook'
                          ](),
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <SectionStack>
              <SectionHint>
                {config.selectedColumns.length > 0
                  ? t[
                      'com.streamlain.automation.sidebar.mapping.no-inputs-columns'
                    ]({ count: String(config.selectedColumns.length) })
                  : t['com.streamlain.automation.sidebar.mapping.no-inputs']()}
              </SectionHint>
              {config.selectedColumns.length > 0 && (
                <Button
                  block
                  onClick={handlePassWholeRow}
                  data-testid="input-pass-whole-row"
                >
                  {t['com.streamlain.automation.sidebar.mapping.whole-row']()}
                </Button>
              )}
              {openWorkflow && (
                <Button block onClick={openWorkflow}>
                  {t['com.streamlain.automation.sidebar.open-workflow']()}
                </Button>
              )}
            </SectionStack>
          )}
        </CollapsibleSection>
      </SettingSection>
    );

  const isSelfRelated = (table: InputTable) =>
    !!config.sourceTableId && table.tableId === config.sourceTableId;
  const relatedTables =
    config.sourceTableId && !isWebhook
      ? secondaryTables.filter(t => t.linkedVia && !isSelfRelated(t))
      : [];
  const invalidInputs = secondaryTables.filter(
    table => !table.linkedVia || isWebhook || !config.sourceTableId
  );
  // Linked before the rule existed: the source table as its own related
  // source. It blocks the run until it is removed.
  const selfInputs =
    config.sourceTableId && !isWebhook
      ? secondaryTables.filter(t => t.linkedVia && isSelfRelated(t))
      : [];
  const sourceTableLabel =
    config.sourceTableName ?? t['com.streamlain.streamlain.input.source']();
  const relationLabel = (columnName: string, tableName: string) => (
    <span className={panelStyles.relationLabel}>
      <span className={panelStyles.relationName}>{columnName}</span>
      <span>{' → '}</span>
      <span className={panelStyles.relationName}>{tableName}</span>
    </span>
  );
  const cardinalityChip = (cardinality: InputTable['cardinality']) => (
    <span
      className={panelStyles.cardinalityChip}
      title={
        cardinality === 'many'
          ? t['com.streamlain.streamlain.input.relation-many']()
          : t['com.streamlain.streamlain.input.auto-detected-relation']()
      }
    >
      {cardinality === 'many' ? 'N' : '1'}
    </span>
  );
  const addRelationButton = onAddRelationColumn && (
    <Button
      variant="secondary"
      className={panelStyles.settingAction}
      prefix={<PlusIcon />}
      onClick={onAddRelationColumn}
      data-testid="add-relation-column"
    >
      {t['com.streamlain.automation.panel.related.add-relation']()}
    </Button>
  );

  return (
    <>
      <SettingUnit name="primary-table" tiles={PRIMARY_TABLE_TILES}>
        <SettingSection tile="source" section="input-tables">
          <CollapsibleSection
            title={t['com.streamlain.streamlain.input.source']()}
            onInfoClick={() =>
              setTimeout(() => registry.start('automation:inputSource'), 50)
            }
          >
            {isWebhook ? (
              <div className={panelStyles.settingBody}>
                <span className={panelStyles.settingLabel}>
                  {t['com.streamlain.automation.sidebar.source-webhook']({
                    name:
                      webhookName ??
                      t['com.streamlain.automation.sidebar.webhook-select'](),
                  })}
                </span>
                <span className={panelStyles.settingHint}>
                  {t['com.streamlain.automation.sidebar.source-webhook-hint']()}
                </span>
              </div>
            ) : (
              <SourceTableField config={config} onUpdate={onUpdate} />
            )}
          </CollapsibleSection>
        </SettingSection>

        <SettingSection tile="tracking" section="status-tracking">
          <StatusTrackingSection
            config={config}
            onUpdate={onUpdate}
            required={needsStatusTracking(config)}
            onDeactivateSchedule={onDeactivateSchedule}
            selectColumns={primarySourceColumns.filter(
              col => col.type === 'select'
            )}
            usedColumns={usedColumns}
            onNavigateToConflict={onNavigateToConflict}
            onCreateStatusColumn={onCreateStatusColumn}
            onInfoClick={() => registry.start('automation:statusTracking')}
          />
        </SettingSection>

        <SettingSection tile="filter" section="filters">
          <NativeFilterPanel
            sourceTableId={config.sourceTableId}
            filterGroup={config.filterGroup}
            onChange={handleFilterChange}
            onInfoClick={() => registry.start('automation:inputFilter')}
            systemFilter={
              hasSystemFilter && systemFilterColumnName
                ? {
                    columnName: systemFilterColumnName,
                    filterValue: statusTracking.successValue,
                  }
                : null
            }
          />
        </SettingSection>

        <SettingSection tile="rows" section="advanced">
          <CollapsibleSection
            title={t['com.streamlain.automation.panel.tile.rows']()}
            onInfoClick={() => registry.start('automation:inputBatch')}
          >
            <div className={panelStyles.settingBody} data-section="batch-size">
              <AnimatedPillSegment<RowsMode>
                active={rowsMode}
                ariaLabel={t['com.streamlain.automation.panel.tile.rows']()}
                onChange={handleRowsMode}
                options={[
                  {
                    key: 'row',
                    label: t['com.streamlain.automation.panel.rows.next'](),
                  },
                  {
                    key: 'batch',
                    label: t['com.streamlain.automation.panel.rows.all'](),
                  },
                ]}
              />
              <AnimatedRegion expanded={rowsMode === 'batch'} keepMounted>
                <label className={panelStyles.rowsLimitField}>
                  <input
                    type="number"
                    min={BATCH_LIMIT_MIN}
                    max={BATCH_LIMIT_MAX}
                    step="1"
                    value={batchLimit}
                    onChange={handleBatchLimit}
                    className={panelStyles.rowsLimitInput}
                    aria-label={t[
                      'com.streamlain.automation.panel.rows.limit'
                    ]()}
                  />
                  {t['com.streamlain.automation.panel.rows.limit']()}
                </label>
                {selectedSchema && (
                  <span
                    className={panelStyles.settingHint}
                    data-testid="rows-run-shape"
                  >
                    {runsOnceForAll(selectedSchema)
                      ? t['com.streamlain.automation.panel.rows.once-for-all']()
                      : t['com.streamlain.automation.panel.rows.per-row']()}
                  </span>
                )}
              </AnimatedRegion>
              <AnimatedPillSegment<InputOrder['direction']>
                active={orderDirection}
                ariaLabel={t[
                  'com.streamlain.automation.panel.rows.order.label'
                ]()}
                onChange={handleOrder}
                options={[
                  {
                    key: 'asc',
                    label:
                      t['com.streamlain.automation.panel.rows.order.oldest'](),
                  },
                  {
                    key: 'desc',
                    label:
                      t['com.streamlain.automation.panel.rows.order.newest'](),
                  },
                ]}
              />
              <span className={panelStyles.settingHint}>
                {t['com.streamlain.automation.panel.rows.order.hint']()}
              </span>
            </div>
          </CollapsibleSection>
        </SettingSection>
      </SettingUnit>

      <SettingUnit name="related-tables" tiles={RELATED_TILES}>
        <SettingSection tile="related" section="related-tables">
          <CollapsibleSection
            title={t['com.streamlain.automation.panel.tile.related']()}
            onInfoClick={() => registry.start('automation:inputRelated')}
          >
            {isWebhook ? (
              <SectionNotice>
                {t['com.streamlain.automation.sidebar.related-webhook']()}
              </SectionNotice>
            ) : (
              <div className={panelStyles.settingBody}>
                {relatedTables.map(table => {
                  const viaColumn =
                    sourceColumns?.find(column => column.id === table.linkedVia)
                      ?.name ?? table.tableName;
                  return (
                    <div
                      key={table.id}
                      className={panelStyles.relationRow}
                      data-testid="related-source-row"
                    >
                      <Menu
                        items={availableRelations.map(relation => (
                          <MenuItem
                            key={relation.columnId}
                            onClick={() =>
                              onUpdate(
                                changeRelatedInputTable(
                                  config.inputTables ?? [],
                                  config.inputMappings ?? [],
                                  {
                                    operation: 'switch',
                                    id: table.id,
                                    table: {
                                      linkedVia: relation.columnId,
                                      tableId: relation.targetTableId,
                                      tableName: relation.targetTableName,
                                      docId: relation.targetDocId,
                                      cardinality: relation.cardinality,
                                    },
                                  }
                                )
                              )
                            }
                            suffix={cardinalityChip(relation.cardinality)}
                          >
                            {relationLabel(
                              relation.columnName,
                              relation.targetTableName
                            )}
                          </MenuItem>
                        ))}
                      >
                        <button
                          type="button"
                          className={panelStyles.relatedSource}
                          disabled={availableRelations.length === 0}
                        >
                          <span className={panelStyles.relatedSourceName}>
                            {table.tableName}
                          </span>
                          <span className={panelStyles.relatedSourceVia}>
                            {table.cardinality === 'many'
                              ? t[
                                  'com.streamlain.automation.panel.related.via-many'
                                ]({ column: viaColumn })
                              : t[
                                  'com.streamlain.automation.panel.related.via-one'
                                ]({ column: viaColumn })}
                          </span>
                        </button>
                      </Menu>
                      <SectionRemoveButton
                        onClick={() => handleRemoveSecondaryTable(table.id)}
                        title={t[
                          'com.streamlain.streamlain.common.remove-table'
                        ]()}
                      />
                    </div>
                  );
                })}

                {!config.sourceTableId && relatedTables.length === 0 && (
                  <p
                    className={panelStyles.settingHint}
                    data-testid="related-needs-source"
                  >
                    {t[
                      'com.streamlain.automation.panel.related.needs-source'
                    ]()}
                  </p>
                )}
                {config.sourceTableId &&
                  (availableRelations.length > 0 ? (
                    <Menu
                      items={
                        <>
                          {availableRelations.map(relation => (
                            <MenuItem
                              key={relation.columnId}
                              onClick={() => handleAddRelatedTable(relation)}
                              suffix={cardinalityChip(relation.cardinality)}
                            >
                              {relationLabel(
                                relation.columnName,
                                relation.targetTableName
                              )}
                            </MenuItem>
                          ))}
                          {onAddRelationColumn && (
                            <>
                              <MenuSeparator />
                              <MenuItem
                                prefixIcon={<PlusIcon />}
                                onClick={onAddRelationColumn}
                              >
                                {t[
                                  'com.streamlain.automation.panel.related.add-relation'
                                ]()}
                              </MenuItem>
                            </>
                          )}
                        </>
                      }
                    >
                      <Button
                        variant="secondary"
                        className={panelStyles.settingAction}
                        prefix={<PlusIcon />}
                        data-testid="add-related-source"
                      >
                        {t[
                          'com.streamlain.streamlain.input.add-related-table'
                        ]()}
                      </Button>
                    </Menu>
                  ) : (
                    <>
                      {/* Every relation column feeds one related source;
                          a further source needs a further relation. */}
                      <p
                        className={panelStyles.settingHint}
                        data-testid={
                          relatedTables.length === 0
                            ? 'related-empty'
                            : 'related-all-linked'
                        }
                      >
                        {relatedTables.length === 0
                          ? t['com.streamlain.automation.panel.related.empty']({
                              table: sourceTableLabel,
                            })
                          : t[
                              'com.streamlain.automation.panel.related.all-linked'
                            ]({ table: sourceTableLabel })}
                      </p>
                      {addRelationButton}
                    </>
                  ))}
                {config.sourceTableId && selfRelationNames.length > 0 && (
                  <p
                    className={panelStyles.settingHint}
                    data-testid="related-self-hint"
                  >
                    {t['com.streamlain.automation.panel.related.self-hint']({
                      column: selfRelationNames.join(', '),
                    })}
                  </p>
                )}
              </div>
            )}
            {selfInputs.map(table => (
              <SectionTableRow
                key={table.id}
                action={
                  <SectionRemoveButton
                    onClick={() => handleRemoveSecondaryTable(table.id)}
                    title={t['com.streamlain.streamlain.common.remove-table']()}
                  />
                }
              >
                <SectionNotice>
                  {t['com.streamlain.automation.sidebar.relation-self']()}
                </SectionNotice>
              </SectionTableRow>
            ))}
            {invalidInputs.map(table => (
              <SectionTableRow
                key={table.id}
                action={
                  <SectionRemoveButton
                    onClick={() => handleRemoveSecondaryTable(table.id)}
                    title={t['com.streamlain.streamlain.common.remove-table']()}
                  />
                }
              >
                <SectionNotice>
                  {table.tableName}:{' '}
                  {t['com.streamlain.automation.sidebar.relation-required']()}
                </SectionNotice>
              </SectionTableRow>
            ))}
          </CollapsibleSection>
        </SettingSection>
      </SettingUnit>
    </>
  );
};
